import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { queryDocuments, streamQuery, postFeedback, postChipClick } from '../../api/client';
import DashMascot from '../Common/DashMascot';

const _apiRaw = process.env.REACT_APP_API_URL || '';
const API_BASE = _apiRaw && !/^https?:\/\//i.test(_apiRaw) ? `https://${_apiRaw}` : _apiRaw;

const MIN_W = 300, MIN_H = 400;
const DEFAULT_W = 360, DEFAULT_H = 520;

/* ── Keyframes ───────────────────────────────────────────────────────── */
if (!document.getElementById('cw-keyframes')) {
  const s = document.createElement('style');
  s.id = 'cw-keyframes';
  s.textContent = `
    @keyframes cwSlideUp  { from{opacity:0;transform:translateY(20px) scale(0.97)} to{opacity:1;transform:none} }
    @keyframes cwFadeIn   { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:none} }
    @keyframes cwBlink    { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes cwPulse    { 0%,100%{box-shadow:0 0 0 0 rgba(29,156,156,0.45)} 70%{box-shadow:0 0 0 10px rgba(29,156,156,0)} }
    @keyframes cwShimmer  { 0%{background-position:-300px 0} 100%{background-position:300px 0} }
    @keyframes cwDot      { 0%,80%,100%{transform:scale(0);opacity:0.3} 40%{transform:scale(1);opacity:1} }
    @keyframes cwPhaseFade{ from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:none} }
    @keyframes cwRecPulse { 0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,0.7)} 70%{box-shadow:0 0 0 10px rgba(220,38,38,0)} }
    .cw-resize-handle { position:absolute; top:0; left:0; width:18px; height:18px; cursor:nw-resize; z-index:10; }
    .cw-resize-handle::after {
      content:''; position:absolute; top:4px; left:4px;
      width:10px; height:10px;
      border-top:2px solid rgba(255,255,255,0.5);
      border-left:2px solid rgba(255,255,255,0.5);
      border-radius:2px 0 0 0;
    }
    .cw-fb-btn {
      background:none; border:none; cursor:pointer;
      width:24px; height:24px;
      border-radius:50%;
      line-height:0;
      display:inline-flex; align-items:center; justify-content:center;
      transition: background 0.18s, transform 0.18s;
    }
    .cw-fb-btn:hover { background:#f1f5f9; }
    .cw-fb-btn:active { transform: scale(0.85); }
    .cw-fb-btn img {
      width:14px; height:14px;
      opacity:0.75;
      transition: opacity 0.18s, filter 0.18s, transform 0.18s;
      display:block;
    }
    .cw-fb-btn:hover img { opacity:1; }
    .cw-fb-btn.active-up {
      background: #16a34a;
      box-shadow: 0 2px 6px rgba(22,163,74,0.35);
      animation: cwPop 0.32s ease;
    }
    .cw-fb-btn.active-up img {
      opacity:1;
      filter: brightness(0) invert(1);            /* solid white on green */
    }
    .cw-fb-btn.active-down {
      background: #dc2626;
      box-shadow: 0 2px 6px rgba(220,38,38,0.35);
      animation: cwPop 0.32s ease;
    }
    .cw-fb-btn.active-down img {
      opacity:1;
      filter: brightness(0) invert(1);            /* solid white on red */
    }
    @keyframes cwPop {
      0%   { transform: scale(1); }
      45%  { transform: scale(1.25); }
      100% { transform: scale(1); }
    }
  `;
  document.head.appendChild(s);
}

/* ── Relative time ───────────────────────────────────────────────────── */
function relTime(ts) {
  const d = Math.floor((Date.now() - ts) / 1000);
  if (d < 10)  return 'just now';
  if (d < 60)  return `${d}s ago`;
  const m = Math.floor(d / 60);
  return m === 1 ? '1 minute ago' : `${m} minutes ago`;
}

/* ── Voice input (Web Speech API) ────────────────────────────────────── */
function useVoiceInput({ onTranscript, onFinal }) {
  const recRef = useRef(null);
  const [listening, setListening] = useState(false);
  const supported = typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = useCallback(() => {
    if (!supported || listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (e) => {
      let interim = '', final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t; else interim += t;
      }
      onTranscript?.(final || interim);
      if (final) onFinal?.(final);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
  }, [supported, listening, onTranscript, onFinal]);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setListening(false);
  }, []);

  return { supported: !!supported, listening, start, stop };
}

/* ── Retrieving indicator ────────────────────────────────────────────── */
const PHASES = [
  { icon: '🔍', label: 'Searching knowledge base' },
  { icon: '📄', label: 'Analyzing documents'      },
  { icon: '✍️', label: 'Generating response'      },
];

function RetrievingIndicator() {
  const [phase, setPhase] = useState(0);
  const [key,   setKey]   = useState(0);
  useEffect(() => {
    const t = setInterval(() => { setPhase(p => (p + 1) % PHASES.length); setKey(k => k + 1); }, 1400);
    return () => clearInterval(t);
  }, []);
  const { icon, label } = PHASES[phase];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'linear-gradient(135deg,#f0fafa,#e6f7f7)', border:'1px solid var(--teal)', borderRadius:10, marginBottom:6, animation:'cwFadeIn 0.2s ease' }}>
      <span style={{ fontSize:16 }}>{icon}</span>
      <span key={key} style={{ fontSize:13, fontWeight:600, color:'var(--teal-dark)', animation:'cwPhaseFade 0.35s ease', flex:1 }}>{label}</span>
      <span style={{ display:'flex', gap:3, alignItems:'center' }}>
        {[0,1,2].map(i => (
          <span key={i} style={{ display:'inline-block', width:6, height:6, background:'var(--teal)', borderRadius:'50%', animation:`cwDot 1.2s ease-in-out ${i*0.18}s infinite` }} />
        ))}
      </span>
    </div>
  );
}

/* ── Custom markdown components ──────────────────────────────────────── */
const mdComponents = {
  /* ## Section header → bold title + divider */
  h2: ({ children }) => (
    <div style={{ marginTop:14, marginBottom:10 }}>
      <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:14, color:'#0f172a', marginBottom:6, letterSpacing:'-0.01em' }}>{children}</div>
      <div style={{ height:1, background:'#e2e8f0' }} />
    </div>
  ),
  h3: ({ children }) => (
    <div style={{ marginTop:10, marginBottom:8 }}>
      <div style={{ fontFamily:'var(--font-heading)', fontWeight:700, fontSize:13, color:'#0f172a', marginBottom:5, letterSpacing:'-0.01em' }}>{children}</div>
      <div style={{ height:1, background:'#e2e8f0' }} />
    </div>
  ),

  /* Blockquote fallback — plain text, no colored box */
  blockquote: ({ children }) => (
    <div style={{ margin:'6px 0', color:'#334155' }}>{children}</div>
  ),

  /* Links — underlined blue with ↗ icon */
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      style={{ color:'#0070f3', textDecoration:'underline', textUnderlineOffset:2, display:'inline-flex', alignItems:'center', gap:3, fontWeight:500 }}>
      {children}
      <img src="/share.png" alt="" style={{ width:13, height:13, display:'inline-block', verticalAlign:'middle' }} />
    </a>
  ),

  /* Ordered list with proper spacing */
  ol: ({ children }) => (
    <ol style={{ paddingLeft:22, margin:'10px 0', fontSize:13.5, lineHeight:1.85, listStyleType:'decimal' }}>{children}</ol>
  ),

  /* Unordered — circle bullets for nested */
  ul: ({ children }) => (
    <ul style={{ paddingLeft:22, margin:'8px 0', fontSize:13.5, lineHeight:1.85, listStyleType:'circle' }}>{children}</ul>
  ),

  li: ({ children }) => <li style={{ marginBottom:9, paddingLeft:2 }}>{children}</li>,

  strong: ({ children }) => (
    <strong style={{ fontWeight:700, color:'#0f172a' }}>{children}</strong>
  ),

  p: ({ children }) => (
    <p style={{ margin:'10px 0', lineHeight:1.75, fontSize:13.5, color:'#334155' }}>{children}</p>
  ),

  hr: () => <div style={{ height:1, background:'#e2e8f0', margin:'10px 0' }} />,

  code: ({ inline, children }) => inline ? (
    <code style={{ background:'#f1f5f9', padding:'1px 5px', borderRadius:4, fontSize:12, color:'#0d7377', fontFamily:'monospace' }}>{children}</code>
  ) : (
    <pre style={{ background:'#f1f5f9', padding:'10px 12px', borderRadius:8, overflowX:'auto', fontSize:12, color:'#1e293b', margin:'8px 0' }}>
      <code>{children}</code>
    </pre>
  ),
};

/* ── Page image ──────────────────────────────────────────────────────── */
function PageImage({ docId, pageNum }) {
  const [state, setState] = useState('idle');
  const [src, setSrc]     = useState(null);
  const load = () => {
    if (state !== 'idle') return;
    setState('loading');
    const token = localStorage.getItem('access_token');
    const headers = token ? { Authorization:`Bearer ${token}` } : {};
    fetch(`${API_BASE}/api/documents/${docId}/page/${pageNum}/image`, { headers })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(b => { setSrc(URL.createObjectURL(b)); setState('loaded'); })
      .catch(() => setState('error'));
  };
  if (state === 'idle')    return <button onClick={load} style={{ marginTop:6, padding:'5px 10px', background:'var(--teal-light)', border:'1px dashed var(--teal)', borderRadius:6, color:'var(--teal-dark)', fontSize:11, cursor:'pointer' }}>🖼️ View page {pageNum}</button>;
  if (state === 'loading') return <div style={{ marginTop:6, height:100, borderRadius:6, background:'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize:'300px 100%', animation:'cwShimmer 1.2s infinite' }} />;
  if (state === 'error')   return <div style={{ marginTop:6, fontSize:11, color:'#e53e3e' }}>Could not load image</div>;
  return <img src={src} alt={`p${pageNum}`} style={{ marginTop:6, width:'100%', borderRadius:6, border:'1px solid #e2e8f0' }} />;
}

/* ── Source card ─────────────────────────────────────────────────────── */
function SourceCard({ src }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom:6, border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden', fontSize:12 }}>
      <button onClick={() => setOpen(p => !p)} style={{ width:'100%', display:'flex', alignItems:'center', gap:6, padding:'7px 10px', background:open?'var(--teal-light)':'#f8fafc', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span>📄</span>
        <span style={{ flex:1, fontWeight:600, color:'var(--navy)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{src.file_name}</span>
        <span style={{ padding:'2px 8px', background:'var(--teal)', color:'#fff', borderRadius:20, fontSize:10, fontWeight:700, flexShrink:0 }}>p.{src.page_number}</span>
        <span style={{ color:'#94a3b8', fontSize:10 }}>{open?'▲':'▼'}</span>
      </button>
      {open && (
        <div style={{ padding:'8px 10px', borderTop:'1px solid #e2e8f0', background:'#fff' }}>
          {src.excerpt && <div style={{ padding:'6px 8px', background:'#f8fafc', borderLeft:'3px solid var(--teal)', borderRadius:4, color:'#475569', fontStyle:'italic', marginBottom:6, lineHeight:1.6 }}>"{src.excerpt}"</div>}
          {src.doc_id  && <PageImage docId={src.doc_id} pageNum={src.page_number} />}
        </div>
      )}
    </div>
  );
}

/* ── Quick-reply chips (PRODUCT-ONLY — role chips removed) ───────────── */
const PRODUCT_CHIPS = [
  'InCommand', 'InQuiry', 'InScribe', 'InSync', 'ESOne Mobile',
  'ShadowLink', 'ShadowPrint', 'Perform', 'E-bridge',
];

// Aliases / common variants users might type for each canonical product.
// Used by detectProductInText below to auto-pick a product from the user's
// free-text message so we never ask them to choose again.
const PRODUCT_ALIASES = {
  'InCommand':    [/\bin\s*command\b/i, /\bincmd\b/i],
  'InQuiry':      [/\bin\s*quiry\b/i, /\binquiry\b/i],
  'InScribe':     [/\bin\s*scribe\b/i, /\binscribe\b/i],
  'InSync':       [/\bin\s*sync\b/i, /\binsync\b/i],
  'ESOne Mobile': [/\b(esone|es\s*one|e-?sone)\s*mobile\b/i, /\bes-?one\b/i],
  'ShadowLink':   [/\bshadow\s*link\b/i],
  'ShadowPrint':  [/\bshadow\s*print\b/i],
  'Perform':      [/\bperform(?:\s+platform)?\b/i],
  'E-bridge':     [/\be\s*-?\s*bridge\b/i, /\bebridge\b/i],
};

/** Return the first canonical product name we can detect inside `text`, or null. */
function detectProductInText(text) {
  if (!text) return null;
  for (const product of PRODUCT_CHIPS) {
    for (const pattern of (PRODUCT_ALIASES[product] || [])) {
      if (pattern.test(text)) return product;
    }
  }
  return null;
}

function extractChips(/* text, currentProduct */) {
  // Product / role chips are intentionally disabled.
  // DASH should answer the user's question directly — never render a
  // product picker. See system-prompt §2.1 for the matching backend rule.
  return [];
}

function QuickReplyChips({ groups, onPick, disabled, priorQuestion, onProductPick }) {
  if (!groups?.length) return null;
  const buildMessage = (groupPlatform, choice) => {
    const isProductGroup = groupPlatform === 'Pick your product';
    if (isProductGroup) {
      return priorQuestion
        ? `${priorQuestion} — for ${choice}`
        : `For ${choice}`;
    }
    return priorQuestion
      ? `${priorQuestion} — I'm a ${choice} on ${groupPlatform}`
      : `I'm a ${choice} on ${groupPlatform}.`;
  };
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {groups.map((g, gi) => (
        <div key={gi}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            {g.platform}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {g.roles.map(r => (
              <button
                key={r}
                disabled={disabled}
                onClick={() => {
                  postChipClick(r, g.platform).catch(() => {});
                  if (g.platform === 'Pick your product') onProductPick?.(r);
                  onPick(buildMessage(g.platform, r));
                }}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: disabled ? '#f1f5f9' : 'linear-gradient(135deg,#1d9c9c,#0d7377)',
                  color: disabled ? '#94a3b8' : '#fff',
                  border: 'none', fontSize: 12, fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  boxShadow: disabled ? 'none' : '0 2px 6px rgba(13,115,119,0.25)',
                  transition: 'transform 0.12s',
                }}
                onMouseEnter={e => { if (!disabled) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
              >{r}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Message bubble ──────────────────────────────────────────────────── */
function Bubble({ msg, onQuickReply, quickReplyDisabled, priorUserQuestion, onProductPick, currentProduct }) {
  const isUser = msg.role === 'user';
  const [feedback, setFeedback] = useState(null);   // 'up' | 'down' | null
  const [tick,     setTick]     = useState(0);
  const [showFbModal, setShowFbModal] = useState(false);  // popup for thumbs-down details

  /* update relative time every 30 s */
  useEffect(() => {
    if (isUser) return;
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, [isUser]);

  return (
    <div style={{ display:'flex', flexDirection:isUser?'row-reverse':'row', gap:8, marginBottom:16, animation:'cwFadeIn 0.2s ease' }}>
      {/* Avatar */}
      <div style={{
        width:32, height:32, borderRadius:'50%',
        background: isUser ? 'var(--teal)' : '#ffffff',
        border: isUser ? 'none' : '1px solid rgba(29,156,156,0.25)',
        display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:13, flexShrink:0, alignSelf:'flex-start', marginTop:2,
        boxShadow: isUser ? 'none' : '0 1px 3px rgba(13,79,82,0.10)',
        overflow:'hidden',
      }}>
        {isUser ? '👤' : <DashMascot size={26} title="DASH assistant" />}
      </div>

      <div style={{ maxWidth:'80%' }}>
        {/* Bubble body */}
        <div style={{
          padding: isUser ? '10px 14px' : '12px 14px',
          borderRadius: isUser ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
          background: isUser ? 'var(--teal)' : '#ffffff',
          color: isUser ? '#fff' : '#0f172a',
          fontSize: 13.5,
          lineHeight: 1.65,
          border: isUser ? 'none' : '1px solid #e2e8f0',
          boxShadow: isUser ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {isUser ? (
            msg.content
          ) : (
            <>
              {msg.isRetrieving && !msg.content && <RetrievingIndicator />}
              {msg.content && (
                <ReactMarkdown components={mdComponents}>{msg.content}</ReactMarkdown>
              )}
              {msg.isStreaming && (
                <span style={{ display:'inline-block', width:2, height:14, background:'var(--teal)', marginLeft:2, verticalAlign:'middle', animation:'cwBlink 1s infinite' }} />
              )}
              {!msg.isStreaming && onQuickReply && (
                <QuickReplyChips
                  groups={extractChips(msg.content, currentProduct)}
                  onPick={onQuickReply}
                  disabled={quickReplyDisabled}
                  priorQuestion={priorUserQuestion}
                  onProductPick={onProductPick}
                />
              )}
            </>
          )}
        </div>

        {/* Sources */}
        {msg.sources?.length > 0 && (
          <div style={{ marginTop:8 }}>
            <div style={{ fontSize:10, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Sources</div>
            {msg.sources.map((s, i) => <SourceCard key={i} src={s} />)}
          </div>
        )}

        {/* Footer: feedback + timestamp (assistant only, after streaming done) */}
        {!isUser && !msg.isStreaming && (
          <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:6 }}>
            <span style={{ fontSize:11, fontWeight:600, color:'#64748b', marginRight:2 }}>
              Was this resolved?
            </span>
            <button
              className={`cw-fb-btn${feedback==='up'?' active-up':''}`}
              onClick={() => {
                const next = feedback === 'up' ? null : 'up';
                setFeedback(next);
                if (next) postFeedback(msg.queryId ?? null, 'up').catch(() => {});
              }}
              title="Helpful"
            ><img src="/like.png" alt="thumbs up" /></button>
            <button
              className={`cw-fb-btn${feedback==='down'?' active-down':''}`}
              onClick={() => {
                if (feedback === 'down') {
                  // Toggle off — clear the local state, no popup.
                  setFeedback(null);
                  return;
                }
                // Show the popup; we'll mark feedback='down' on submit.
                setShowFbModal(true);
              }}
              title="Not helpful — tell us more"
            ><img src="/negative-vote.png" alt="thumbs down" /></button>
            {msg.timing && (
              <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, color:'#475569', marginLeft:2 }}>
                <img src="/clock.png" alt="" style={{ width:12, height:12, opacity:0.85 }} />
                {msg.timing < 1000 ? `${msg.timing}ms` : `${(msg.timing/1000).toFixed(1)}s`}
              </span>
            )}
            <span style={{ fontSize:10, color:'#94a3b8', marginLeft:'auto' }}>
              {msg.createdAt ? relTime(msg.createdAt) : ''}
            </span>
          </div>
        )}

        {showFbModal && (
          <FeedbackModal
            queryId={msg.queryId ?? null}
            onClose={() => setShowFbModal(false)}
            onSubmitted={() => { setFeedback('down'); setShowFbModal(false); }}
          />
        )}
      </div>
    </div>
  );
}

/* ── Feedback popup (thumbs-down) ────────────────────────────────────── */
function FeedbackModal({ queryId, onClose, onSubmitted }) {
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [comment,   setComment]   = useState('');
  const [sending,   setSending]   = useState(false);
  const [err,       setErr]       = useState('');

  const validate = () => {
    if (!comment.trim()) return 'Please tell us what went wrong.';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email.';
    return '';
  };

  const submit = async () => {
    const v = validate();
    if (v) { setErr(v); return; }
    setErr('');
    setSending(true);
    try {
      await postFeedback(queryId, 'down', comment.trim(), {
        first_name: firstName.trim() || null,
        last_name:  lastName.trim()  || null,
        email:      email.trim()     || null,
      });
      onSubmitted?.();
    } catch (e) {
      setErr('Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="dash-fb-modal"
      style={{
        position:'fixed', inset:0, zIndex:3000,
        background:'rgba(15,23,42,0.45)', backdropFilter:'blur(3px)',
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:16,
        animation:'cwFadeIn 0.16s ease',
      }}
    >
      <style>{`
        .dash-fb-modal input::placeholder,
        .dash-fb-modal textarea::placeholder { color: #cbd5e1; opacity: 1; font-weight: 400; }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:'min(440px, 100%)',
          background:'#fff',
          borderRadius:16,
          boxShadow:'0 30px 60px rgba(13,79,82,0.30)',
          overflow:'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding:'16px 20px',
          background:'linear-gradient(135deg, #0d4f52, #1d9c9c)',
          color:'#fff',
        }}>
          <div style={{ fontSize:15, fontWeight:800, letterSpacing:'-0.01em' }}>
            Help us improve DASH
          </div>
          <div style={{ fontSize:12, opacity:0.85, marginTop:3 }}>
            Tell us what went wrong with this answer. Contact info is optional.
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'18px 20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            <Field label="First name">
              <input
                value={firstName} onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane" style={fbInput}
              />
            </Field>
            <Field label="Last name">
              <input
                value={lastName} onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe" style={fbInput}
              />
            </Field>
          </div>

          <Field label="Email">
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              style={fbInput}
            />
          </Field>

          <Field label="What was wrong with the answer?" required>
            <textarea
              value={comment} onChange={(e) => setComment(e.target.value)}
              rows={5}
              placeholder="E.g., the steps don't match the latest UI, the URL is broken, the answer is for the wrong product…"
              style={{ ...fbInput, resize:'vertical', fontFamily:'inherit' }}
            />
          </Field>

          {err && (
            <div style={{
              padding:'8px 12px', marginTop:6,
              background:'#fff5f5', border:'1px solid #feb2b2',
              borderRadius:8, color:'#c53030', fontSize:12.5,
            }}>{err}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding:'12px 20px', borderTop:'1px solid #e2e8f0',
          display:'flex', justifyContent:'flex-end', gap:8,
        }}>
          <button
            onClick={onClose}
            disabled={sending}
            style={{
              padding:'8px 16px', border:'1px solid #cbd5e1', background:'#fff',
              borderRadius:'var(--radius-btn, 10px)', color:'#475569',
              fontSize:13.5, fontWeight:600, cursor:'pointer',
            }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={sending || !comment.trim()}
            style={{
              padding:'8px 20px',
              background:'linear-gradient(135deg, #0d4f52, #1d9c9c)',
              color:'#fff', border:'none',
              borderRadius:'var(--radius-btn, 10px)',
              fontSize:13.5, fontWeight:700,
              cursor: (sending || !comment.trim()) ? 'not-allowed' : 'pointer',
              opacity: (sending || !comment.trim()) ? 0.55 : 1,
            }}
          >{sending ? 'Sending…' : 'Send feedback'}</button>
        </div>
      </div>
    </div>
  );
}

const fbInput = {
  width:'100%', padding:'8px 11px',
  border:'1px solid #cbd5e1', borderRadius:8,
  fontSize:13.5, outline:'none', boxSizing:'border-box',
  background:'#fff', color:'#0f172a',
};
function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{
        fontSize:11, fontWeight:700, color:'#475569',
        textTransform:'uppercase', letterSpacing:'0.06em',
        marginBottom:5,
      }}>
        {label}{required && <span style={{ color:'#c53030', marginLeft:4 }}>*</span>}
      </div>
      {children}
    </div>
  );
}

/* ── Main widget ─────────────────────────────────────────────────────── */
let _id = 0;
const uid = () => ++_id;

const savedSize = () => {
  try {
    const s = JSON.parse(localStorage.getItem('cw-size') || '{}');
    return {
      w: Math.min(Math.max(s.w || DEFAULT_W, MIN_W), window.innerWidth  - 40),
      h: Math.min(Math.max(s.h || DEFAULT_H, MIN_H), window.innerHeight - 40),
    };
  } catch { return { w: DEFAULT_W, h: DEFAULT_H }; }
};

/**
 * ChatWidget props
 *   embedded (default false) — when true, the chat panel takes over the full
 *   viewport (no floating button, no resize, no maximize toggle).  Used by
 *   the /chat-embed route which is loaded inside an iframe on other DH sites.
 *   Default behavior (embedded=false) is byte-for-byte identical to today.
 */
export default function ChatWidget({ embedded = false } = {}) {
  const [open,      setOpen]     = useState(embedded);   // embed mode is always open
  const [maximized, setMax]      = useState(false);
  const [size,      setSize]     = useState(savedSize);
  const buildWelcome = () => ({
    id: uid(), role: 'assistant', createdAt: Date.now(),
    content: `**Hi, I'm DASH — your DeliverHealth support assistant.** Tell me what you're running into and I'll help you sort it out.`,
  });
  const [messages,  setMessages] = useState([buildWelcome()]);
  const [loading, setLoading] = useState(false);
  const [input,   setInput]   = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);

  const performReset = useCallback(() => {
    setMessages([buildWelcome()]);
    setSelectedProduct(null);
    setInput('');
    setSize({ w: DEFAULT_W, h: DEFAULT_H });
    setResetConfirm(false);
  }, []);
  const [micTipSeen, setMicTipSeen] = useState(() => {
    try { return localStorage.getItem('cw-mic-tip-seen') === '1'; } catch { return true; }
  });
  const dismissMicTip = useCallback(() => {
    setMicTipSeen(true);
    try { localStorage.setItem('cw-mic-tip-seen', '1'); } catch {}
  }, []);

  const bottomRef   = useRef(null);
  const inputRef    = useRef(null);
  const resizing    = useRef(false);
  const resizeStart = useRef({});
  const messagesRef = useRef(messages);

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 80); }, [open]);
  useEffect(() => { localStorage.setItem('cw-size', JSON.stringify(size)); }, [size]);

  /* ── Resize ─────────────────────────────────────────────────────── */
  const startResize = useCallback((e) => {
    e.preventDefault();
    resizing.current = true;
    resizeStart.current = { x:e.clientX, y:e.clientY, w:size.w, h:size.h };
    const onMove = (ev) => {
      if (!resizing.current) return;
      const maxW = window.innerWidth  - 40;
      const maxH = window.innerHeight - 40;
      setSize({
        w: Math.min(Math.max(resizeStart.current.w + (resizeStart.current.x - ev.clientX), MIN_W), maxW),
        h: Math.min(Math.max(resizeStart.current.h + (resizeStart.current.y - ev.clientY), MIN_H), maxH),
      });
    };
    const onUp = () => {
      resizing.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [size]);

  /* ── Message helpers ────────────────────────────────────────────── */
  const appendMsg  = (msg) => setMessages(p => [...p, { id:uid(), ...msg }]);
  const updateLast = (fn)  => setMessages(p => { const c=[...p]; c[c.length-1]=fn(c[c.length-1]); return c; });

  /* Ensure numbered steps land on their own lines regardless of LLM formatting */
  const fixSteps = (text) =>
    text.replace(/([.:!?])\s*(\d{1,2}\.\s+)/g, '$1\n$2').trim();

  const send = useCallback(async (text) => {
    const q = text.trim();
    if (!q || loading) return;
    setInput('');

    // Auto-detect a product mention in the user's message. If found, treat
    // it as the active product (overrides any prior selection) so we never
    // re-ask. The "Asking about X" badge updates automatically.
    const detectedProduct = detectProductInText(q);
    const effectiveProduct = detectedProduct || selectedProduct;
    if (detectedProduct && detectedProduct !== selectedProduct) {
      setSelectedProduct(detectedProduct);
    }

    // Build conversation history (last 8 real turns) BEFORE appending the new question.
    const history = messagesRef.current
      .filter(m => (m.role === 'user' || m.role === 'assistant') && m.content && m.content.trim())
      .slice(-8)
      .map(m => ({ role: m.role, content: m.content }));
    appendMsg({ role:'user', content:q, createdAt:Date.now() });
    setLoading(true);
    const startedAt = Date.now();
    appendMsg({ role:'assistant', content:'', isStreaming:true, isRetrieving:true, createdAt:startedAt });
    try {
      await streamQuery(q, null,
        (chunk)   => updateLast(m => ({ ...m, content:m.content+chunk, isRetrieving:false })),
        (sources) => updateLast(m => ({ ...m, sources })),
        ()        => updateLast(m => ({ ...m, content: fixSteps(m.content), isStreaming:false, isRetrieving:false, timing: Date.now() - startedAt })),
        (queryId) => updateLast(m => ({ ...m, queryId })),
        history,
        effectiveProduct,
      );
    } catch {
      try {
        const res = await queryDocuments(q, null, history, effectiveProduct);
        updateLast(() => ({ id:uid(), role:'assistant', content:fixSteps(res.data.answer), sources:res.data.sources, timing:res.data.processing_time_ms, isStreaming:false, isRetrieving:false, createdAt:Date.now() }));
      } catch {
        updateLast(m => ({ ...m, content:'Sorry, an error occurred. Please try again.', isStreaming:false, isRetrieving:false }));
      }
    }
    setLoading(false);
  }, [loading, selectedProduct]);

  const handleKey = (e) => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(input); } };

  /* ── Voice input ──────────────────────────────────────────────────── */
  const voice = useVoiceInput({
    onTranscript: (text) => setInput(text),
    onFinal: (text) => {
      // auto-send when user finishes speaking
      const final = text.trim();
      if (final) {
        setInput('');
        send(final);
      }
    },
  });
  const toggleMic = () => voice.listening ? voice.stop() : voice.start();

  // Embed mode forces full-bleed; otherwise honor user resize / maximize.
  const panelW = embedded
    ? '100%'
    : (maximized ? window.innerWidth  - 40 : size.w);
  const panelH = embedded
    ? '100%'
    : (maximized ? window.innerHeight - 40 : Math.min(size.h, window.innerHeight - 110));

  // Panel positioning differs between modes:
  //   embedded  → fills the iframe (position:relative inside the iframe body)
  //   floating  → fixed bottom-right with shadow / radius (today's behavior)
  const panelStyle = embedded ? {
    position:'absolute', inset:0, zIndex:1,
    width:'100%', height:'100%',
    background:'#f8fafc',
    display:'flex', flexDirection:'column', overflow:'hidden',
    fontFamily:'var(--font-body)',
  } : {
    position:'fixed', bottom:maximized?20:90, right:maximized?20:28, zIndex:999,
    width:panelW, height:panelH,
    background:'#f8fafc',
    borderRadius:16,
    boxShadow:'0 8px 48px rgba(0,0,0,0.18)',
    display:'flex', flexDirection:'column',
    overflow:'hidden',
    animation:'cwSlideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)',
    border:'1px solid var(--border)',
    fontFamily:'var(--font-body)',
  };

  return (
    <>
      {/* ── Floating button — hidden in embed mode (host page provides its own) ── */}
      {!embedded && (
        <button
          id="dash-chat-trigger"
          onClick={() => setOpen(p => !p)}
          style={{ position:'fixed', bottom:28, right:28, zIndex:1000, display:'flex', alignItems:'center', gap:10, padding:'12px 22px 12px 14px', background:'var(--teal)', color:'#fff', borderRadius:'var(--radius-btn)', fontSize:15, fontWeight:700, boxShadow:'0 4px 20px rgba(29,156,156,0.45)', transition:'background 0.15s, transform 0.1s', animation:'cwPulse 2.5s infinite', border:'none', cursor:'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.background='var(--teal-hover)'; e.currentTarget.style.transform='scale(1.04)'; }}
          onMouseLeave={e => { e.currentTarget.style.background='var(--teal)'; e.currentTarget.style.transform='none'; }}
        >
          {open ? (
            <span style={{ fontSize:18, lineHeight:1 }}>✕</span>
          ) : (
            <span style={{
              width:30, height:30, borderRadius:8,
              background:'#fff',
              display:'flex', alignItems:'center', justifyContent:'center',
              flexShrink:0,
            }}>
              <DashMascot size={24} title="DASH" />
            </span>
          )}
          {open ? 'Close' : 'Chat'}
        </button>
      )}

      {/* ── Chat panel ── */}
      {open && (
        <div style={panelStyle}>

          {/* Resize handle — not shown in embed mode (iframe owns its own size) */}
          {!maximized && !embedded && <div className="cw-resize-handle" onMouseDown={startResize} title="Drag to resize" />}

          {/* ── Header ── */}
          <div style={{ background:'linear-gradient(135deg,#0d4f52,#1d9c9c)', padding:'12px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:34, height:34, background:'#ffffff', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 1px 3px rgba(0,0,0,0.10)' }}>
                <DashMascot size={28} title="DASH" />
              </div>
              <div>
                <div style={{ color:'#fff', fontFamily:'var(--font-heading)', fontWeight:700, fontSize:14, lineHeight:1.2, letterSpacing:'-0.01em' }}>DASH</div>
                <div style={{ color:'rgba(255,255,255,0.65)', fontSize:11 }}>DeliverHealth Support Assistant</div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:8, height:8, background:'#4ade80', borderRadius:'50%', boxShadow:'0 0 0 2px rgba(74,222,128,0.3)' }} />
              {!embedded && (
                <button onClick={() => setMax(p => !p)} title={maximized?'Restore':'Maximize'}
                  style={{ width:26, height:26, borderRadius:6, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.15)'}
                >{maximized ? '⊡' : '⤢'}</button>
              )}
              {embedded && (
                <button onClick={() => window.parent?.postMessage({ type: 'dash:close' }, '*')} title="Close"
                  style={{ width:26, height:26, borderRadius:6, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
                  onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.15)'}
                >✕</button>
              )}
              {!maximized && (
                <div style={{ position:'relative' }}>
                  <button onClick={() => setResetConfirm(p => !p)} title="Reset chat"
                    style={{ width:26, height:26, borderRadius:6, background: resetConfirm ? 'rgba(255,255,255,0.32)' : 'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
                    onMouseLeave={e => { if (!resetConfirm) e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
                  >↺</button>
                  {resetConfirm && (
                    <div style={{
                      position:'absolute',
                      top:'calc(100% + 10px)',
                      right:0,
                      width:240,
                      padding:'10px 12px',
                      background:'linear-gradient(135deg, #0d4f52 0%, #1d9c9c 100%)',
                      color:'#fff',
                      borderRadius:10,
                      boxShadow:'0 8px 24px rgba(13,79,82,0.30)',
                      fontSize:12,
                      lineHeight:1.45,
                      animation:'cwFadeIn 0.18s ease',
                      zIndex:6,
                    }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                        <span style={{ fontSize:14, lineHeight:1 }}>⚠️</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, marginBottom:2 }}>Reset chat?</div>
                          <div style={{ opacity:0.92 }}>This clears all messages in this conversation and starts fresh.</div>
                          <div style={{ display:'flex', gap:6, marginTop:8 }}>
                            <button
                              onClick={performReset}
                              style={{
                                padding:'4px 12px', borderRadius:999,
                                background:'#fff', color:'#0d4f52',
                                border:'none', fontSize:11, fontWeight:700,
                                cursor:'pointer',
                              }}
                            >Yes, reset</button>
                            <button
                              onClick={() => setResetConfirm(false)}
                              style={{
                                padding:'4px 12px', borderRadius:999,
                                background:'rgba(255,255,255,0.18)', color:'#fff',
                                border:'1px solid rgba(255,255,255,0.35)',
                                fontSize:11, fontWeight:600,
                                cursor:'pointer',
                              }}
                            >No</button>
                          </div>
                        </div>
                      </div>
                      {/* Pointer triangle */}
                      <div style={{
                        position:'absolute', top:-6, right:8,
                        width:12, height:12,
                        background:'#0d4f52',
                        transform:'rotate(45deg)',
                        boxShadow:'-2px -2px 4px rgba(0,0,0,0.04)',
                      }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Messages ── */}
          <div style={{ flex:1, overflowY:'auto', padding:'20px 20px 12px', background:'#f8fafc' }}>
            {messages.map((m, i) => {
              // Find the most recent user message BEFORE this one — used to
              // re-inject context when a clarifier chip is tapped.
              let priorUserQuestion;
              for (let j = i - 1; j >= 0; j--) {
                if (messages[j].role === 'user') {
                  priorUserQuestion = messages[j].content;
                  break;
                }
              }
              return (
                <Bubble
                  key={m.id}
                  msg={m}
                  onQuickReply={i === messages.length - 1 ? send : undefined}
                  quickReplyDisabled={loading}
                  priorUserQuestion={priorUserQuestion}
                  onProductPick={setSelectedProduct}
                  currentProduct={selectedProduct}
                />
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div style={{ height:1, background:'#e2e8f0', flexShrink:0 }} />

          {/* ── Input ── */}
          <div style={{
            padding:'14px 20px',
            background:'rgba(255,255,255,0.65)',
            backdropFilter:'blur(14px) saturate(180%)',
            WebkitBackdropFilter:'blur(14px) saturate(180%)',
            borderTop:'1px solid rgba(226,232,240,0.6)',
            flexShrink:0,
          }}>
            {selectedProduct && (
              <div style={{
                display:'inline-flex', alignItems:'center', gap:6,
                marginBottom:8,
                padding:'4px 10px 4px 12px',
                borderRadius:999,
                background:'linear-gradient(135deg, #1d9c9c, #0d7377)',
                color:'#fff',
                fontSize:11.5, fontWeight:700,
                boxShadow:'0 2px 6px rgba(13,115,119,0.25)',
              }}>
                <span style={{ fontSize:9, opacity:0.8, textTransform:'uppercase', letterSpacing:'0.06em' }}>Asking about</span>
                <span>{selectedProduct}</span>
                <button
                  onClick={() => setSelectedProduct(null)}
                  title="Clear product filter"
                  style={{
                    width:16, height:16, borderRadius:'50%',
                    background:'rgba(255,255,255,0.25)',
                    border:'none', color:'#fff',
                    fontSize:11, lineHeight:1, cursor:'pointer',
                    display:'inline-flex', alignItems:'center', justifyContent:'center',
                    padding:0,
                  }}
                >×</button>
              </div>
            )}
            <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Type your message…"
                disabled={loading}
                rows={1}
                style={{ flex:1, padding:'9px 12px', background:'rgba(248,250,252,0.65)', backdropFilter:'blur(8px)', WebkitBackdropFilter:'blur(8px)', border:'1px solid rgba(226,232,240,0.8)', borderRadius:10, fontSize:13.5, color:'#0f172a', resize:'none', maxHeight:100, overflowY:'auto', lineHeight:1.5, transition:'border-color 0.15s', outline:'none' }}
                onFocus={e => { e.target.style.borderColor='var(--teal)'; }}
                onBlur={e  => { e.target.style.borderColor='#e2e8f0'; }}
                onInput={e => { e.target.style.height='auto'; e.target.style.height=Math.min(e.target.scrollHeight,100)+'px'; }}
              />
              {voice.supported && (
                <div style={{ position:'relative', flexShrink:0 }}>
                  {!micTipSeen && !voice.listening && (
                    <div style={{
                      position:'absolute',
                      bottom:'calc(100% + 10px)',
                      right:0,
                      width:220,
                      padding:'10px 12px',
                      background:'linear-gradient(135deg, #0d4f52 0%, #1d9c9c 100%)',
                      color:'#fff',
                      borderRadius:10,
                      boxShadow:'0 8px 24px rgba(13,79,82,0.30)',
                      fontSize:12,
                      lineHeight:1.4,
                      animation:'cwFadeIn 0.25s ease',
                      zIndex:5,
                    }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
                        <span style={{ fontSize:14, lineHeight:1 }}>💡</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, marginBottom:2 }}>Tip: Speak your question</div>
                          <div style={{ opacity:0.9 }}>Click the mic and just talk — DASH will type it for you.</div>
                          <button
                            onClick={dismissMicTip}
                            style={{
                              marginTop:6, padding:'3px 10px', borderRadius:999,
                              background:'rgba(255,255,255,0.18)', color:'#fff',
                              border:'1px solid rgba(255,255,255,0.35)',
                              fontSize:11, fontWeight:600, cursor:'pointer',
                            }}
                          >Got it</button>
                        </div>
                      </div>
                      {/* Pointer triangle */}
                      <div style={{
                        position:'absolute', bottom:-6, right:14,
                        width:12, height:12,
                        background:'#1d9c9c',
                        transform:'rotate(45deg)',
                        boxShadow:'2px 2px 4px rgba(0,0,0,0.06)',
                      }} />
                    </div>
                  )}
                  <button
                    onClick={() => { dismissMicTip(); toggleMic(); }}
                    disabled={loading}
                    title={voice.listening ? 'Stop listening' : 'Speak your question'}
                    style={{
                      width:36, height:36, borderRadius:10,
                      background: voice.listening ? '#dc2626' : 'rgba(203,213,225,0.72)',
                      backdropFilter: voice.listening ? 'none' : 'blur(8px)',
                      WebkitBackdropFilter: voice.listening ? 'none' : 'blur(8px)',
                      color: voice.listening ? '#fff' : '#475569',
                      border:'none', display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:16, flexShrink:0, cursor: loading ? 'not-allowed' : 'pointer',
                      transition:'all 0.15s',
                      animation: voice.listening
                        ? 'cwRecPulse 1.4s infinite'
                        : (micTipSeen ? 'none' : 'cwPulse 2s infinite'),
                      boxShadow: !micTipSeen && !voice.listening ? '0 0 0 3px rgba(29,156,156,0.25)' : 'none',
                    }}
                  >{voice.listening
                    ? <span style={{ width:10, height:10, background:'#fff', borderRadius:2, display:'inline-block' }} />
                    : <img src="/microphone.png" alt="mic" style={{ width:18, height:18, display:'block' }} />}
                  </button>
                </div>
              )}
              <button
                onClick={() => send(input)}
                disabled={loading || !input.trim()}
                style={{ width:36, height:36, borderRadius:10, background:(loading||!input.trim())?'rgba(186,196,209,0.72)':'var(--teal)', backdropFilter:(loading||!input.trim())?'blur(8px)':'none', WebkitBackdropFilter:(loading||!input.trim())?'blur(8px)':'none', color:(loading||!input.trim())?'#94a3b8':'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, cursor:(loading||!input.trim())?'not-allowed':'pointer', transition:'all 0.15s' }}
              >{loading ? '⏳' : '➤'}</button>
            </div>
            <div style={{ textAlign:'center', fontSize:10, color:'#94a3b8', marginTop:6 }}>
              {voice.listening
                ? '🎙 Listening… speak your question'
                : voice.supported
                  ? 'Enter to send · Shift+Enter for new line · click the mic to speak'
                  : 'Enter to send · Shift+Enter for new line'}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
